import {
  LitElement,
  classMap,
  createRef,
  css,
  html,
  nothing,
  ref,
  when,
} from '@lit'
import * as paths from '@nova/paths'
import styles from '@nova/styles'
import { viewportObserver } from '@nova/utils'
import '@vendor/media-chrome'
import '@vendor/media-chrome-vimeo'
import '@vendor/media-chrome-youtube'

class NovaAdaptiveVideo extends LitElement {
  static properties = {
    sectionId: {
      type: String,
      attribute: 'section-id',
    },
    srcDesktop: {
      type: String,
      attribute: 'src-desktop',
    },
    aspectRatioDesktop: {
      type: String,
      attribute: 'aspect-ratio-desktop',
    },
    srcMobile: {
      type: String,
      attribute: 'src-mobile',
    },
    aspectRatioMobile: {
      type: String,
      attribute: 'aspect-ratio-mobile',
    },
    srcType: {
      type: String,
      attribute: 'src-type',
    },
    posterDesktop: {
      type: String,
      attribute: 'poster-desktop',
    },
    posterMobile: {
      type: String,
      attribute: 'poster-mobile',
    },
    descriptionDesktop: {
      type: String,
      attribute: 'description-desktop',
    },
    descriptionMobile: {
      type: String,
      attribute: 'description-mobile',
    },
    loadingType: {
      type: String,
      attribute: 'loading',
    },
    viewport: {
      type: String,
      state: true,
    },
    autoplay: {
      type: Boolean,
    },
    loop: {
      type: Boolean,
    },
    sound: {
      type: Boolean,
    },
    controls: {
      type: Boolean,
    },
    captions: {
      type: String,
      attribute: 'captions',
    },
    trackProgress: {
      type: Boolean,
      attribute: 'track-progress',
    },
    background: {
      type: Boolean,
      attribute: 'background',
    },
    adaptContainerHeight: {
      type: Boolean,
      attribute: 'adapt-container-height',
    },
  }

  get activeSrc() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.srcMobile) return this.srcDesktop
    // Return mobile asset if current viewport isn't desktop, else return desktop asset
    return ['mobile', 'tablet'].includes(this.viewport)
      ? this.srcMobile
      : this.srcDesktop
  }

  get activeAspectRatio() {
    // Always fallback on desktop asset if no mobile asset is provided
    if (!this.aspectRatioMobile) return this.aspectRatioDesktop
    // Return mobile asset if current viewport isn't desktop, else return desktop asset
    return ['mobile', 'tablet'].includes(this.viewport)
      ? this.aspectRatioMobile
      : this.aspectRatioDesktop
  }

  get activeDesc() {
    return this.activeSrc === this.srcMobile &&
      this.descriptionMobile !== 'false'
      ? this.descriptionMobile
      : this.activeSrc === this.srcDesktop &&
          this.descriptionDesktop !== 'false'
        ? this.descriptionDesktop
        : ''
  }

  get activePoster() {
    return this.activeSrc === this.srcMobile && this.posterMobile !== 'false'
      ? this.posterMobile
      : this.activeSrc === this.srcDesktop && this.posterDesktop !== 'false'
        ? this.posterDesktop
        : false
  }

  constructor() {
    super()
    this.viewport = viewportObserver.currentViewport
    viewportObserver.addListener(this._handleViewportChange)
    this.srcMobile = false
    this.srcDesktop = false
    this.aspectRatioMobile = false
    this.aspectRatioDesktop = '16/9'
    this.videoRef = createRef()
    this.loadingType = 'lazy'
    this.background = false
    this.adaptContainerHeight = false
  }

  connectedCallback() {
    super.connectedCallback()

    if (this.autoplay) this.autoplaying = true
    this.percentageCompleted = 0
    this.videoTwentyFive = false
    this.videoFifty = false
    this.videoSeventyFive = false
    this.videoComplete = false
    this.videoInView = false
  }

  firstUpdated() {
    this.$video = this.videoRef.value
  }

  _handleViewportChange = (newViewport) => {
    this.viewport = newViewport
  }

  _handlePlay = () => {
    if (!this.autoplaying) {
      // Track play analytics event here
    }
  }

  _handlePause = () => {
    this.autoplaying = false
    // Track pause analytics event here
  }

  _handleProgress = () => {
    if (this.$video?.readyState >= 2 && this.trackProgress) {
      const progressUpdate = () => {
        this.percentageCompleted =
          (this.$video.currentTime / this.$video.duration) * 100
        if (!this.videoTwentyFive && this.percentageCompleted > 25) {
          this.videoTwentyFive = true
          this._handleTrackProgress('25%')
        }
        if (!this.videoFifty && this.percentageCompleted > 50) {
          this.videoFifty = true
          this._handleTrackProgress('50%')
        }
        if (!this.videoSeventyFive && this.percentageCompleted > 75) {
          this.videoSeventyFive = true
          this._handleTrackProgress('75%')
        }
        if (!this.videoComplete && this.percentageCompleted > 99) {
          this.videoComplete = true
          this._handleTrackProgress('100%')
          this.$video.removeEventListener('timeupdate', progressUpdate)
        }
      }
      this.$video.addEventListener('timeupdate', progressUpdate)
    }
  }

  _handleTrackProgress = (progress) => {
    // Track progress analytics event here
  }

  renderYoutubeVideo = () => {
    return html`
      <youtube-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </youtube-video>
    `
  }

  renderVimeoVideo = () => {
    return html`
      <vimeo-video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </vimeo-video>
    `
  }

  renderUploadedVideo = () => {
    return html`
      <video
        ${ref(this.videoRef)}
        part="video-element"
        id="video-element"
        slot="media"
        src=${this.activeSrc}
        loading=${this.loadingType}
        muted
        playsinline
        aria-label=${this.activeDesc}
        ?autoplay=${this.autoplay}
        ?loop=${this.loop}
        @loadeddata=${this._handleProgress}
        @pause=${this._handlePause}
        @play=${this._handlePlay}
      >
        ${this.renderCaptions}
      </video>
    `
  }

  renderCaptions = () => {
    if (this.captions) {
      return html`
        <track
          label="English"
          kind="subtitles"
          srclang="en"
          src="${window.__DNA_GLOBAL__?.domain}/cdn/shop/files/${
            this.captions.split('files/')[2]
          }"
        />`
    }
  }

  renderControls = () => {
    return html`
      <media-control-bar part="controls">
        <media-play-button>
          <span slot="play">${paths.play}</span>
          <span slot="pause">${paths.pause}</span>
        </media-play-button>
        ${when(
          this.sound,
          () => html`
            <media-mute-button>
              <span slot="high">${paths.audioOn}</span>
              <span slot="off">${paths.audioOff}</span>
            </media-mute-button>
          `,
          () => nothing,
        )}
        ${when(
          this.captions,
          () => html`
            <media-captions-button>
              <span slot="on">${paths.captionsOn}</span>
              <span slot="off">${paths.captionsOff}</span>
            </media-captions-button>
          `,
          () => nothing,
        )}
      </media-control-bar>
    `
  }

  play() {
    this.$video.play()
  }

  render() {
    const stateClasses = {
      'background-video': this.background,
      'adapter-height': this.adaptContainerHeight,
    }

    return html`
      <media-controller class="${classMap(
        stateClasses,
      )}" part="media-controller" style="--active-aspect-ratio: ${
        this.activeAspectRatio
      }">
        ${when(
          this.srcType === 'youtube',
          this.renderYoutubeVideo,
          () => nothing,
        )}
        ${when(this.srcType === 'vimeo', this.renderVimeoVideo, () => nothing)}
        ${when(
          this.srcType === 'upload',
          this.renderUploadedVideo,
          () => nothing,
        )}
        ${when(
          this.activePoster,
          () => html`
              <media-poster-image
                slot="poster"
                src=${this.activePoster}>
              </media-poster-image>`,
          () => nothing,
        )}
        ${when(this.controls, this.renderControls, () => nothing)}
      </media-controller>
    `
  }

  static styles = [
    styles.a11y,
    styles.variables,
    css`
      :host {
        display: block;
        height: 100%;
        width: 100%;
      }
      #video-element {
        height: 100%;
        object-fit: contain;
        width: 100%;
      }
      media-controller {
        width: 100%;
        height: auto;
        aspect-ratio: var(--active-aspect-ratio);
        display: block;
      }
      .adapter-height {
        width: auto;
        height: 100%;
        left: 50%;
        transform: translateX(-50%);
      }
      media-control-bar {
        padding: var(--nova-space-2xs);
        gap: var(--nova-space-2xs);
      }
      media-control-bar > * {
        cursor: pointer;
        --media-control-background: transparent;
        --media-control-hover-background: transparent;
        line-height: 0;
        margin-right: calc(var(--nova-space-2xs) - 1px);
        padding: 0;
      }
      media-control-bar > *:last-child {
        margin: 0;
      }
      @media screen and (max-width: 768px) {
        .background-video {
          height: 100%;
          width: auto;
          left: 50%;
          transform: translateX(-50%);
        }
      }
    `,
  ]
}

customElements.define('nova-adaptive-video', NovaAdaptiveVideo)
